/**
 * QrCode component - Payment portal QR code for invoices
 *
 * Uses the 'qrcode' npm package to generate QR codes as base64 images.
 * Returns both the image data (for registering with PdfDocument) and
 * the content structure (for adding to the document).
 */

import QRCode from 'qrcode';
import type { Content, ContentStack, ContentText } from 'pdfmake/interfaces';
import type { QrCodeData } from '../types';

/**
 * Result of QR code generation, includes both image data and content
 */
export interface QrCodeResult {
    /** The image name to use in pdfmake */
    imageName: string;
    /** The base64 data URL of the QR code image */
    imageData: string;
    /** The pdfmake content to add to the document */
    content: Content;
}

/**
 * Generate a QR code as a base64 data URL
 *
 * @param url - The URL to encode
 * @param size - The size in pixels
 * @returns Promise resolving to a data URL
 */
async function generateQrCodeDataUrl(url: string, size: number): Promise<string> {
    try {
        const dataUrl = await QRCode.toDataURL(url, {
            width: size,
            margin: 1,
            errorCorrectionLevel: 'L', // Low error correction for longer URLs
        });
        return dataUrl;
    } catch (error) {
        console.error('Failed to generate QR code:', error);
        throw error;
    }
}

/**
 * Create a QR code for PDF (async)
 * Returns the image data and content separately so the caller can register the image
 *
 * @param data - QR code configuration
 * @returns Promise resolving to QrCodeResult with image data and content
 */
export async function createQrCode(data: QrCodeData): Promise<QrCodeResult> {
    const { url, label = 'Make a Payment', size = 100 } = data;

    // Generate QR code at higher resolution for better scanning
    // The URL can be very long (includes client info, addresses, etc.)
    // Use 400px for generation, display at the requested size
    const generationSize = 400;
    const imageData = await generateQrCodeDataUrl(url, generationSize);
    const imageName = 'qrcode_payment';

    // Display at a larger size for better scannability (minimum 120 points)
    const displaySize = Math.max(size, 120);

    // Create content that references the image by name
    const qrImage = {
        image: imageName,
        width: displaySize,
        alignment: 'center' as const,
    };

    // Create a text link with the label
    const linkText: ContentText = {
        text: label,
        link: url,
        style: 'qrCodeLabel',
        alignment: 'center',
        color: '#1976d2',
        decoration: 'underline',
    };

    const content: ContentStack = {
        stack: [
            qrImage,
            { text: '', margin: [0, 8, 0, 0] },
            linkText,
        ],
        alignment: 'center',
        margin: [0, 15, 0, 15],
    };

    return {
        imageName,
        imageData,
        content,
    };
}

/**
 * Create a barcode section with QR code centered (async)
 * This matches the invoice card's barcode section layout
 *
 * @param data - QR code configuration
 * @returns Promise resolving to QrCodeResult with image data and content
 */
export async function createBarcodeSection(data: QrCodeData): Promise<QrCodeResult> {
    const qrResult = await createQrCode(data);

    const wrappedContent: Content = {
        columns: [
            { width: '*', text: '' },
            {
                width: 'auto',
                stack: [qrResult.content],
            },
            { width: '*', text: '' },
        ],
        margin: [0, 20, 0, 20],
    };

    return {
        imageName: qrResult.imageName,
        imageData: qrResult.imageData,
        content: wrappedContent,
    };
}

export default createQrCode;
